import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { CdkMenuModule } from '@angular/cdk/menu';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import type { ButtonVariant, ButtonColor } from '../button/button.component';
import type { PsUiSize } from '../../types';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { PS_DROPDOWN_MENU_POSITIONS } from '../dropdown/dropdown-menu-positions.constants';
import { PsDropdownTriggerForDirective } from '../dropdown/directives/dropdown-trigger.directive';
import { PsDropdownMenuDirective } from '../dropdown/directives/dropdown-menu.directive';
import { ButtonComponent } from '../button/button.component';
import { DividerComponent } from '../divider/divider.component';
import { IconComponent } from '../icon/icon.component';
import type { PsDropdownButtonMenuItem } from './dropdown-button.types';
import { IconName, SizeProp } from '@fortawesome/angular-fontawesome';

type ResolvedMenuRow =
  | { kind: 'divider'; trackId: string }
  | { kind: 'item'; item: PsDropdownButtonMenuItem; trackId: string };

/**
 * Strict action menu on a button trigger (not for forms). **`menuItems`** use
 * **`PsDropdownButtonMenuItem`** only; **`itemSelected`** emits the chosen item.
 */
@Component({
  selector: 'ps-dropdown-button',
  standalone: true,
  imports: [
    ButtonComponent,
    CdkMenuModule,
    DividerComponent,
    DropdownComponent,
    IconComponent,
    PsDropdownMenuDirective,
    PsDropdownTriggerForDirective,
  ],
  templateUrl: './dropdown-button.component.html',
  styleUrl: './dropdown-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownButtonComponent {
  public readonly dataCy = input.required<string>();
  public readonly menuItems =
    input.required<readonly PsDropdownButtonMenuItem[]>();
  public readonly menuAriaLabel = input<string | null>(null);
  public readonly menuDataCy = input.required<string>();
  public readonly iconName = input<IconName>('chevron-down');

  public readonly variant = input<ButtonVariant>('filled');
  public readonly color = input<ButtonColor>('primary');
  public readonly size = input<PsUiSize>('md');
  public readonly disabled = input(false);
  public readonly triggerAriaLabel = input<string | null>(null);
  public readonly menuPosition = input<ConnectedPosition[] | undefined>(
    undefined,
  );
  public readonly itemSelected = output<PsDropdownButtonMenuItem>();
  protected readonly menuOpen = signal(false);
  protected readonly resolvedMenuPosition = computed(
    () => this.menuPosition() ?? PS_DROPDOWN_MENU_POSITIONS,
  );

  /** Flattened rows: each item, then optional divider when **`hasDivider`**. */
  protected readonly menuEffect = computed(() => {
    return this.menuOpen() && this.iconName() === 'chevron-down';
  });

  protected readonly iconSize = computed<SizeProp>(() => {
    return this.iconName() === 'chevron-down' ? 'sm' : 'lg';
  });

  protected readonly resolvedMenuRows = computed((): ResolvedMenuRow[] => {
    const out: ResolvedMenuRow[] = [];
    const items = this.menuItems();
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      out.push({ kind: 'item', item, trackId: `item-${i}` });
      if (item.hasDivider) {
        out.push({ kind: 'divider', trackId: `divider-${i}` });
      }
    }
    return out;
  });

  protected onRowTriggered(item: PsDropdownButtonMenuItem): void {
    this.itemSelected.emit(item);
  }

  protected onTriggerOpened(): void {
    this.menuOpen.set(true);
  }

  protected onTriggerClosed(): void {
    this.menuOpen.set(false);
  }
}
